import { Star, UtensilsCrossed } from "lucide-react";
import { motion } from "framer-motion";
import type { MenuItem } from "../../types/menu";
import Badge from "../ui/Badge";

interface MenuCardFeaturedProps {
  item: MenuItem;
}

export default function MenuCardFeatured({ item }: MenuCardFeaturedProps) {
  return (
    <motion.article
      className="group relative flex flex-col sm:flex-row bg-white rounded-3xl overflow-hidden shadow-md border border-accent/20"
      initial={{ opacity: 0, x: -30 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{
        boxShadow:
          "0 25px 50px -12px rgba(0,0,0,0.12), 0 0 0 1px rgba(232,168,56,0.3)",
      }}
    >
      {/* Subtle accent glow on hover */}
      <div className="absolute -inset-px rounded-3xl bg-gradient-to-r from-accent/20 via-primary/10 to-accent/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-sm" />

      {/* Image */}
      <div className="relative sm:w-2/5 md:w-[38%] h-60 sm:h-auto overflow-hidden shrink-0">
        {/* Featured ribbon */}
        <motion.div
          className="absolute top-4 left-4 z-10 flex items-center gap-1.5 bg-accent text-primary-dark text-xs font-extrabold px-4 py-2 rounded-xl shadow-lg shadow-accent/30"
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
        >
          <Star className="w-3.5 h-3.5" fill="currentColor" />
          Destacado
        </motion.div>

        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/10 via-sand to-accent/15 flex flex-col items-center justify-center gap-2 min-h-[220px]">
            <UtensilsCrossed className="w-12 h-12 text-warm-gray/25" />
            <span className="text-[11px] text-warm-gray/50 font-semibold tracking-wider uppercase">
              Foto próximamente
            </span>
          </div>
        )}
        {/* Edge gradient */}
        <div className="hidden sm:block absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" />
      </div>

      {/* Content */}
      <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10 sm:w-3/5 md:w-[62%] gap-3">
        {/* Tags */}
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {item.tags.map((tag) => (
              <Badge key={tag} tag={tag} />
            ))}
          </div>
        )}

        <h3 className="font-heading font-bold text-2xl lg:text-3xl text-[var(--color-text-base)] leading-tight">
          {item.name}
        </h3>
        {item.tagline && (
          <p className="text-base font-semibold text-primary leading-snug">
            {item.tagline}
          </p>
        )}

        <p className="text-[var(--color-text-muted)] text-sm sm:text-base leading-relaxed max-w-lg">
          {item.description}
        </p>

        {/* Price */}
        {item.sizes ? (
          <div className="flex flex-wrap gap-2 pt-2">
            {item.sizes.map((size) => (
              <div
                key={size.label}
                className="flex flex-col items-center bg-primary/10 px-3 py-1.5 rounded-xl"
              >
                <span className="text-lg font-black text-primary">
                  ${size.price.toFixed(2)}
                </span>
                <span className="text-[10px] text-warm-gray font-semibold uppercase tracking-wider">
                  {size.label}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-baseline gap-1.5 pt-2">
            <span className="text-3xl lg:text-4xl font-black text-primary">
              ${item.price!.toFixed(2)}
            </span>
            <span className="text-xs text-warm-gray font-semibold uppercase tracking-wider">
              USD
            </span>
          </div>
        )}
      </div>
    </motion.article>
  );
}
